import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { smPut, smPost, smDelete, errorText } from './api.js';

const TIMEZONES = ['Asia/Jerusalem', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Australia/Sydney'];
const SERVER_NAME = { production: 'Production (app.monada.ai)', alpha: 'ALPHA', local: 'A developer\'s machine' };
// The same Google app and scopes as the Gmail integration in the main app (public/index.html).
const GOOGLE_CLIENT_ID = '605788172223-qp2dppdlf9iqns2tkvj4jshiq0evm8db.apps.googleusercontent.com';
const GMAIL_SCOPES = 'https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email';

// Opens Google sign-in in a popup and resolves with the code the server exchanges for a lasting
// (refresh) token. prompt=consent makes Google give that token even if Monada was allowed before.
const googleCode = () => new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) return reject(new Error('Google sign-in hasn\'t loaded. Reload the page and try again.'));
    window.google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GMAIL_SCOPES,
        ux_mode: 'popup',
        prompt: 'consent',
        callback: resp => (resp.error ? reject(new Error(resp.error_description || resp.error)) : resolve(resp.code)),
        error_callback: e => reject(new Error(e?.type === 'popup_closed' ? 'The Google window was closed before connecting.' : (e?.message || 'Google sign-in failed.'))),
    }).requestCode();
});

function SettingsTab({ settings, onSaved }) {
    const [fromName, setFromName] = useState('');
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        if (!settings) return;
        setFromName(settings.mailbox?.fromName || 'Adi Ben Mayor');
        setForm({ dailyLimit: settings.dailyLimit, start: settings.hours.start, end: settings.hours.end, timezone: settings.timezone, weekdaysOnly: settings.weekdaysOnly, signature: settings.signature, unsubscribeLine: settings.unsubscribeLine, runsOn: settings.runsOn });
    }, [settings]);

    const run = async (label, fn) => {
        setBusy(label); setError(null); setInfo(null);
        try { await fn(); } catch (e) { setError(errorText(e)); }
        setBusy(null);
    };

    if (!settings || !form) return <CircularProgress />;
    const connected = settings.mailbox?.connected;

    const connect = () => run('connect', async () => {
        const code = await googleCode();
        const s = await smPut('/settings/mailbox', { code, fromName });
        onSaved({ ...settings, ...s });
        setInfo(`Connected: campaigns are sent from ${s.mailbox.address}, and replies are read from its inbox.`);
    });
    const saveName = () => run('name', async () => {
        const s = await smPut('/settings/mailbox', { fromName });
        onSaved({ ...settings, ...s });
        setInfo('Sender name saved, and the mailbox still works.');
    });
    const disconnect = () => run('disconnect', async () => {
        onSaved({ ...settings, ...(await smDelete('/settings/mailbox')) });
        setInfo('Mailbox disconnected. Running campaigns stop sending until a mailbox is connected again.');
    });
    const test = () => run('test', async () => {
        const r = await smPost('/settings/test-email', { to: settings.mailbox.address });
        setInfo(`Test email sent to ${r.sentTo}. Check that inbox.`);
    });
    const save = () => run('save', async () => {
        const s = await smPut('/settings', { dailyLimit: form.dailyLimit, hours: { start: form.start, end: form.end }, timezone: form.timezone, weekdaysOnly: form.weekdaysOnly, signature: form.signature, unsubscribeLine: form.unsubscribeLine, runsOn: form.runsOn });
        onSaved({ ...settings, ...s });
        setInfo('Settings saved.');
    });

    return (
        <Box sx={{ maxWidth: 760 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Mailbox</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Campaigns are sent from this Gmail account, and replies are read from its inbox. Connect it with Google sign-in, the same way
                    Gmail is connected in Monada. The server keeps Google's permission, so campaigns keep sending when this page is closed.
                </Typography>
                {connected && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Connected: {settings.mailbox.fromName ? `${settings.mailbox.fromName} <${settings.mailbox.address}>` : settings.mailbox.address}
                        {settings.mailbox.checkedAt ? ` · checked ${dayjs(settings.mailbox.checkedAt).format('D MMM YYYY, HH:mm')}` : ''}
                    </Alert>
                )}
                <TextField size="small" label="Sender name" value={fromName} onChange={e => setFromName(e.target.value)} sx={{ minWidth: 320 }}
                    helperText="The name people see next to the address." />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={connect} disabled={!!busy}>
                        {busy === 'connect' ? <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} /> : null}{connected ? 'Connect a different Gmail account' : 'Connect Gmail'}
                    </Button>
                    {connected && fromName !== (settings.mailbox.fromName || '') && <Button variant="outlined" onClick={saveName} disabled={!!busy}>Save the sender name</Button>}
                    {connected && <Button variant="outlined" onClick={test} disabled={!!busy}>Send a test email to myself</Button>}
                    <Box sx={{ flex: 1 }} />
                    {connected && <Button color="error" onClick={disconnect} disabled={!!busy}>Disconnect</Button>}
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Sending</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                    <TextField size="small" type="number" label="Emails a day, at most" value={form.dailyLimit} onChange={e => setForm(f => ({ ...f, dailyLimit: e.target.value }))}
                        helperText="Across all campaigns. Keep it low on a new mailbox." />
                    <TextField size="small" type="number" label="From (hour)" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} />
                    <TextField size="small" type="number" label="Until (hour)" value={form.end} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} />
                    <TextField select size="small" label="Time zone" value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}>
                        {TIMEZONES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                    <FormControlLabel sx={{ gridColumn: 'span 2' }} control={<Switch checked={!!form.weekdaysOnly} onChange={e => setForm(f => ({ ...f, weekdaysOnly: e.target.checked }))} />}
                        label={`Working days only (${form.timezone === 'Asia/Jerusalem' ? 'Sunday to Thursday' : 'Monday to Friday'})`} />
                </Box>
                <TextField multiline minRows={2} fullWidth label="Signature" value={form.signature} onChange={e => setForm(f => ({ ...f, signature: e.target.value }))} sx={{ mt: 2 }} />
                <TextField fullWidth label="Opt-out line, at the end of every email" value={form.unsubscribeLine} onChange={e => setForm(f => ({ ...f, unsubscribeLine: e.target.value }))} sx={{ mt: 2 }}
                    helperText="Required for cold email. Anyone who replies with an opt-out is never written to again, in any campaign." />
                <TextField select size="small" label="Which server sends" value={form.runsOn} onChange={e => setForm(f => ({ ...f, runsOn: e.target.value }))} sx={{ mt: 2, minWidth: 320 }}
                    helperText={`Every server shares the same campaigns; only this one sends and reads replies, so nothing goes out twice. You are on: ${SERVER_NAME[settings.thisServer] || settings.thisServer}.`}>
                    {(settings.servers || []).map(s => <MenuItem key={s} value={s}>{SERVER_NAME[s] || s}</MenuItem>)}
                </TextField>
                <Box sx={{ mt: 2 }}><Button variant="contained" onClick={save} disabled={!!busy}>Save</Button></Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Apollo</Typography>
                <Typography variant="body2" color={settings.apollo ? 'success.main' : 'error'}>
                    {settings.apollo ? 'The Apollo API key is set on this server.' : 'APOLLO_API_KEY is not set on this server: audiences can\'t be searched here.'}
                </Typography>
            </Paper>
        </Box>
    );
}

export default SettingsTab;
